//libs
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';

//types
import { Location } from 'history';

//helpers
import logger from 'shared/logger';
import { log } from 'shared/helpers/newRelic';
import { getCleanUrl, getDynamicPageSlugFromPathname } from 'shared/helpers/utils/url';
import { isIntegerId } from 'shared/helpers/utils';

//constants
import {
  DEFAULT_REQUEST,
  API_REQUEST_PAYLOAD,
  API_KEYS,
  ERROR_CONSTANTS,
  QUERY_PARAMS,
  FILTER_QUERY_CHARS,
} from './constants';
import { TAG_IDENTIFIER } from 'shared/helpers/newRelic/constants';
import { SORT_FILTERS } from 'shared/components/Filter/components/SortView/constants';
import { DEVICE_OS_KEY, DEVICE_OS_VALUE } from 'shared/constants/listing';

//to remove undefined and null values from payload
const getCleanParams = (params: Record<string, string>) => {
  let apiParams: Record<string, string | number | boolean> = {};
  Object.keys(params).forEach((param: string) => {
    if (params[param]) apiParams[param] = params[param];
  });
  return apiParams;
};

//convert url params<string> to object
export const getParamsObject = (search: string) => Object.fromEntries(new URLSearchParams(search));

//logger helper to push logs/error status to kibana / new relic based on the server/client
export const errorLogger = (errorMessage: string, errorMethod: string) => {
  if (__SERVER__)
    logger.error({
      ERROR: ERROR_CONSTANTS.ERROR,
      message: `Error: ${errorMessage} from METHOD:${errorMethod}`,
    });
  else {
    log({
      errTitle: 'Error in errorLogger func',
      tags: {
        [TAG_IDENTIFIER.MODULE]: ERROR_CONSTANTS.IDENTIFIER,
        message: {
          ERROR: ERROR_CONSTANTS.ERROR,
          METHOD_NAME: errorMethod,
          message: errorMessage,
        },
      },
    });
  }
};

//convert url filters into filters object
const getFiltersFromURL = (filters: string) => {
  let filter: Record<string, string> = {};

  filters?.split(FILTER_QUERY_CHARS.FILTERS_SEPARATOR).forEach((i) => {
    const [filterKey, value] = i.split(FILTER_QUERY_CHARS.FILTER_ASSIGNMENT);
    if (isEmpty(filterKey) || isEmpty(value)) return;

    if (filterKey === API_KEYS.PRICE_FILTER) {
      filter[filterKey] = value.replace(
        FILTER_QUERY_CHARS.PRICE_VALUES_JOIN,
        FILTER_QUERY_CHARS.PRICE_PAYLOAD_JOIN
      );
      return;
    }
    if (filterKey === API_KEYS.SORT_FILTER) {
      filter[filterKey] = value.replace(/`.`/g, '_');
      return;
    }
    if (value?.indexOf(FILTER_QUERY_CHARS.FILTER_VALUES_JOIN) > -1) {
      const sanitisedValue = value
        ?.split(FILTER_QUERY_CHARS.FILTER_VALUES_JOIN)
        ?.filter((val) => !isEmpty(val))
        ?.join(FILTER_QUERY_CHARS.FILTER_VALUES_JOIN);

      if (sanitisedValue) {
        filter[filterKey] = sanitisedValue;
      }
      return;
    }

    filter[filterKey] = value;
  });

  return filter;
};

//to make the intial request payload and remove the unwanted params from request
export const makePayload = (params: Record<string, string>) => {
  let defaultParams = { ...DEFAULT_REQUEST };
  let currentPage = isIntegerId(params.p) ? params.p : '1';
  let filters = getFiltersFromURL(params.f);
  if (defaultParams.deviceType === 'WEBSITE') {
    defaultParams[DEVICE_OS_KEY] = DEVICE_OS_VALUE.DESKTOP;
  }

  if (params.coupon_offer_id) {
    defaultParams[API_KEYS.COUPON_OFFER_ID] = params.coupon_offer_id;
  } else if (params.offer_id) {
    defaultParams[API_KEYS.OFFER_ID] = params.offer_id;
  } else if (params.categoryId) {
    defaultParams[API_KEYS.CATEGORY_ID] = params.categoryId;
  } else if (params.dynamicPageSlug) {
    defaultParams[API_KEYS.SEARCH_TERM] = params.dynamicPageSlug;
  } else if (params.q) {
    defaultParams[API_KEYS.SEARCH_TERM] = params.q;
  } else {
    errorLogger(ERROR_CONSTANTS.ERROR_MESSAGE_INVALID_KEY, ERROR_CONSTANTS.MAKE_PAYLOAD_METHOD);
    return;
  }

  Object.keys(params).forEach((key) => {
    if (key.includes(API_KEYS.FILTER)) {
      filters[key] = params[key];
      return;
    }
    if (
      key === API_KEYS.SORT_FILTER &&
      params[API_KEYS.SORT_FILTER] !== DEFAULT_REQUEST[API_KEYS.SORT_FILTER]
    ) {
      filters[API_KEYS.SORT_FILTER] = params[API_KEYS.SORT_FILTER];
      return;
    }
    if (API_REQUEST_PAYLOAD.includes(key)) {
      defaultParams[key] = params[key];
      return;
    }
    if (key === API_KEYS.PAGE_NUMBER) {
      currentPage = params[key];
      return;
    }
  });
  return { defaultParams, currentPage, filters };
};

export const makeUrlParams = (params: any, payload: any) => {
  const searchParams = new URLSearchParams(params);
  Object.keys(payload).forEach((i) => {
    if (!payload[i]) searchParams.delete(i);
    else searchParams.set(i, payload[i]);
  });
  return searchParams.toString();
};

//to get the category id
export const getCategoryId = (pathname: string) => {
  if (pathname.includes(QUERY_PARAMS.CATEGORY_ID_PATTERN)) {
    const urlParams = pathname.split(QUERY_PARAMS.CATEGORY_ID_PATTERN);

    return urlParams[1].split('/')[0];
  }
  return '';
};

export const getDynamicPageSlugObject = (pathname: string) => {
  if (pathname.includes(QUERY_PARAMS.DYNAMIC_PAGE)) {
    return { dynamicPageSlug: getDynamicPageSlugFromPathname(pathname) };
  }
  return {};
};

export const isOfferPage = (pathname: string) => {
  return pathname.includes(QUERY_PARAMS.OFFER_ID_PATTERN);
};

//to get the offer id
export const getOfferId = (pathname: string) => {
  if (isOfferPage(pathname)) {
    const urlParams = pathname.split(QUERY_PARAMS.OFFER_ID_PATTERN);

    return urlParams[1].split('/')[0];
  }
  return '';
};

const getIdObject = (pathname: string) => {
  const categoryId = getCategoryId(pathname) || '';
  if (categoryId?.length) return { categoryId };
  const offer_id = getOfferId(pathname) || '';
  if (offer_id?.length) return { offer_id };
  return { categoryId };
};

//creates final object of url params
export const getURLQuery = (url: string, pathname: string) => {
  const categoryOrOfferId = getIdObject(pathname);
  const params = {
    ...Object.fromEntries(new URLSearchParams(url)),
    ...categoryOrOfferId,
    ...getDynamicPageSlugObject(pathname),
  };
  const filters = getFiltersFromURL(params.f || '');
  return getCleanParams({ ...params, ...filters });
};

//converts applied filter object into a string
export const getAppliedFiltersString = (appliedFilters: Record<string, string>) => {
  const appliedFiltersArray = Object.keys(appliedFilters).map((af) => {
    if (af === API_KEYS.PRICE_FILTER) return `${af}=${appliedFilters[af].replace('-', '@')}`;
    if (af === API_KEYS.SORT_FILTER) return `${af}=${appliedFilters[af].replace('_', '`.`')}`;
    if (appliedFilters[af]) return `${af}=${appliedFilters[af]}_`;
  });

  return appliedFiltersArray.filter(Boolean).join(';');
};

//create the final payload for api request
//function merges the recieved payload and location params and creates a final object
//calls make paylaod to get the final object of params
//calls getCleanparams to filter out blanks and empty values
export const transformPayload = (
  requestPayload: Record<string, string>,
  location: Location,
  isMobile: boolean
) => {
  const urlParams = getURLQuery(location.search, location.pathname);
  const result = { deviceType: isMobile ? 'MSITE' : 'WEBSITE', ...urlParams, ...requestPayload };
  return makePayload(getCleanParams(result));
};

//helper function to check whether page is search page or not
export const isSearchPage = (pathname: string) => pathname.includes(QUERY_PARAMS.SEARCH_PATTERN);

//extract search text from url
export const getSearchText = (search: string) =>
  new URLSearchParams(search).get(QUERY_PARAMS.SEARCH) || '';

//extract filters from url
export const getIsFilteredURL = (search: string) =>
  new URLSearchParams(search).get(QUERY_PARAMS.FILTER) || '';

export const appendURLParameters = (url: string, param: string) => {
  if (isEmpty(url) || isEmpty(param)) return '';
  const params = new URLSearchParams(param);
  const cleanUrl = getCleanUrl(url);

  if (cleanUrl.indexOf('?') > -1) {
    return `${cleanUrl}&${params.toString()}`;
  }

  return `${cleanUrl}?${params.toString()}`;
};

export const getSelectedSort = (appliedFilters: Record<string, string>) => {
  const sortValue = get(appliedFilters, 'sort');
  if (!sortValue) return SORT_FILTERS[0];

  let sort = SORT_FILTERS.find((obj) => obj.value === sortValue);

  if (sort !== undefined) {
    return sort;
  }

  return SORT_FILTERS[0];
};
